import { useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { SeeItInAction } from "@/components/SeeItInAction";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Switch } from "@/components/ui/switch";
import { Loader2, ClipboardCheck, CheckCircle2, XCircle, Mail, Siren, Plus, Trash2, Wand2, Rocket, Sparkles, ChevronDown, X, RotateCcw, ShieldCheck } from "lucide-react";
import confetti from "canvas-confetti";
import Markdown from "@/components/Markdown";
import { useAuth } from "@/context/AuthContext";

const COACHING_TIPS = {
  trueup_on_cadence: "Run a True-Up from the project header — it re-confirms task progress with your team in minutes.",
  no_stale_overdue: "Tackle tasks overdue by more than a week first: update their status, re-date them, or split them into smaller steps.",
  flagged_have_risks: "Open each At Risk / Jeopardy task and link a risk with an owner and mitigation, so the exposure is visible.",
  all_assigned: "Give every task an owner — unowned work is the #1 cause of silent slippage.",
  baseline_set: "Set your baseline in the Plan view once dates look right — it's what makes slippage measurable later.",
  readout_scheduled: "Turn on the weekly status read-out (in Audit settings) or send an exec summary so stakeholders stay in the loop.",
  resources_managed: "Add your people under People → Resources and assign them to tasks so workload stays visible.",
  artifacts_on_file: "Upload your charter and key documents to Artifacts — the PMO archive makes them searchable forever.",
  charter_attached: "Attach the signed project charter, then tick the confirmation — it anchors scope and authority.",
  weekly_meetings: "Schedule a recurring weekly project meeting under Meet, then confirm it here.",
  doc1_attached: "Attach Project Doc 1 so governance can trace the essentials.",
};

const LEVELS = [
  { min: 1, name: "Maestro Level", color: "#10B981" },
  { min: 0.9, name: "Nearly Maestro", color: "#10B981" },
  { min: 0.7, name: "Strong Practice", color: "#0EA5E9" },
  { min: 0.4, name: "Building Momentum", color: "#F59E0B" },
  { min: 0, name: "Getting Started", color: "#F97316" },
];

const levelFor = (audit) => {
  const ratio = audit.total ? audit.met / audit.total : 0;
  const idx = LEVELS.findIndex((l) => ratio >= l.min);
  return { idx: idx === -1 ? LEVELS.length - 1 : idx, level: LEVELS[idx === -1 ? LEVELS.length - 1 : idx] };
};

const fireConfetti = () => {
  confetti({ particleCount: 120, spread: 75, origin: { y: 0.65 }, zIndex: 9999 });
  setTimeout(() => confetti({ particleCount: 60, spread: 110, origin: { y: 0.6 }, zIndex: 9999 }), 250);
};

export function LevelUpPanel({ audit, projectId, onCoached }) {
  const [coaching, setCoaching] = useState(false);
  const ratio = audit.total ? audit.met / audit.total : 0;
  const level = LEVELS.find((l) => ratio >= l.min) || LEVELS[LEVELS.length - 1];
  const unmet = audit.standards.filter((s) => !s.met)
    .sort((a, b) => (b.weight || 1) - (a.weight || 1) || (b.auto === a.auto ? 0 : a.auto ? -1 : 1));
  const nextMoves = unmet.slice(0, 3);

  const coachMe = async () => {
    setCoaching(true);
    try {
      const { data } = await api.post(`/projects/${projectId}/audit/coaching`);
      onCoached?.(data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Coaching is unavailable right now");
    } finally { setCoaching(false); }
  };

  return (
    <div className="border border-border rounded-lg p-5 space-y-4" data-testid="levelup-panel">
      <div className="flex items-center gap-4">
        <span className="h-12 w-12 rounded-xl flex items-center justify-center shrink-0" style={{ background: `${level.color}1a` }}>
          <Rocket className="h-5 w-5" style={{ color: level.color }} />
        </span>
        <div className="min-w-0 flex-1">
          <p className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground">Level up</p>
          <p className="font-display text-lg font-extrabold tracking-tight" style={{ color: level.color }} data-testid="levelup-level">{level.name}</p>
          <p className="text-xs text-muted-foreground" data-testid="levelup-progress">
            {audit.met} practice{audit.met === 1 ? "" : "s"} in place{unmet.length > 0 ? ` · ${unmet.length} to go` : " — all covered"}
          </p>
        </div>
      </div>
      <div className="flex gap-1" aria-hidden>
        {audit.standards.map((s) => (
          <span key={s.key} className="h-1.5 flex-1 rounded-full" style={{ background: s.met ? level.color : "#E2E8F0" }} />
        ))}
      </div>
      {nextMoves.length > 0 && (
        <div className="space-y-2" data-testid="levelup-next-moves">
          <p className="text-xs font-bold uppercase tracking-wide text-muted-foreground">Next best moves</p>
          {nextMoves.map((s) => (
            <div key={s.key} className="rounded-lg bg-secondary/40 px-3 py-2" data-testid={`levelup-move-${s.key}`}>
              <p className="text-sm font-semibold">{s.label}{(s.weight || 1) > 1 && <span className="ml-1.5 text-[10px] font-bold px-1.5 py-0.5 rounded bg-amber-100 text-amber-700">high impact</span>}</p>
              <p className="text-xs text-muted-foreground mt-0.5">{COACHING_TIPS[s.key] || (s.auto ? s.detail : "Complete this practice, then tick the confirmation box below.")}</p>
            </div>
          ))}
        </div>
      )}
      <div className="pt-1 border-t border-border/60">
        <Button size="sm" variant="outline" className="gap-1.5 w-full mt-3" onClick={coachMe} disabled={coaching} data-testid="levelup-coach-button">
          {coaching ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4 text-amber-500" />}
          {coaching ? "Maestro is thinking…" : audit.coaching ? "Refresh my coaching plan" : "Coach me — build my action plan"}
        </Button>
        {audit.coaching && (
          <div className="mt-3 rounded-lg border border-amber-200 bg-amber-50/50 p-3" data-testid="levelup-coaching">
            <Markdown text={audit.coaching} className="text-sm" />
            {audit.coaching_at && <p className="text-[10px] text-muted-foreground mt-2">Coached {String(audit.coaching_at).slice(0, 10)}</p>}
          </div>
        )}
      </div>
    </div>
  );
}

export const scoreColor = (s) => (s >= 80 ? "#10B981" : s >= 50 ? "#F59E0B" : "#EF4444");

export function AuditChecklist({ audit, onToggleManual, readOnly = false, onRequestRemove, onDecideExclusion, onRestore, canModerate = false }) {
  const [openKey, setOpenKey] = useState(null);
  const auto = audit.standards.filter((s) => s.auto);
  const manual = audit.standards.filter((s) => !s.auto);
  const Row = ({ s }) => {
    const open = openKey === s.key;
    const ex = s.exclusion;
    const removed = ex?.status === "approved";
    const pending = ex?.status === "pending";
    return (
      <div className={`border-t border-border ${removed ? "opacity-50" : ""}`} data-testid={`audit-standard-${s.key}`}>
        <div className="flex items-center gap-3 px-4 py-2.5 cursor-pointer hover:bg-secondary/30 transition-colors group/auditrow"
             onClick={() => setOpenKey(open ? null : s.key)} data-testid={`audit-standard-row-${s.key}`}>
          {s.auto ? (
            s.met ? <CheckCircle2 className="h-4 w-4 shrink-0" style={{ color: "#10B981" }} /> : <XCircle className="h-4 w-4 shrink-0" style={{ color: "#EF4444" }} />
          ) : (
            <span onClick={(e) => e.stopPropagation()}>
              <Checkbox checked={s.met} disabled={readOnly || removed} onCheckedChange={(v) => onToggleManual?.(s.key, !!v)} data-testid={`audit-manual-${s.key}`} />
            </span>
          )}
          <div className="min-w-0 flex-1">
            <p className={`text-sm font-medium ${removed ? "line-through" : ""}`}>{s.label}</p>
            <p className="text-xs text-muted-foreground">{s.detail}</p>
          </div>
          {removed && (
            <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-slate-200 text-slate-600 shrink-0" title={`Removed by ${ex.decided_by_name || "supervisor"}${ex.reason ? ` — ${ex.reason}` : ""}`} data-testid={`audit-removed-${s.key}`}>Removed</span>
          )}
          {pending && (
            <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-amber-100 text-amber-700 shrink-0" title={`Removal requested by ${ex.requested_by_name}${ex.reason ? ` — ${ex.reason}` : ""}`} data-testid={`audit-pending-${s.key}`}>Removal pending</span>
          )}
          {pending && canModerate && !readOnly && (
            <span className="flex items-center gap-1 shrink-0" onClick={(e) => e.stopPropagation()}>
              <button className="text-[10px] font-bold px-2 py-0.5 rounded bg-emerald-600 text-white hover:bg-emerald-700" onClick={() => onDecideExclusion?.(s.key, true)} data-testid={`audit-approve-removal-${s.key}`}>Approve</button>
              <button className="text-[10px] font-bold px-2 py-0.5 rounded bg-secondary hover:bg-slate-200" onClick={() => onDecideExclusion?.(s.key, false)} data-testid={`audit-keep-${s.key}`}>Keep</button>
            </span>
          )}
          {removed && canModerate && !readOnly && onRestore && (
            <button className="shrink-0 p-1 rounded hover:bg-secondary" title="Restore this criterion" onClick={(e) => { e.stopPropagation(); onRestore(s.key); }} data-testid={`audit-restore-${s.key}`}><RotateCcw className="h-3.5 w-3.5 text-muted-foreground" /></button>
          )}
          {!ex && !readOnly && onRequestRemove && (
            <button className="shrink-0 p-1 rounded opacity-0 group-hover/auditrow:opacity-100 hover:bg-red-50 transition-opacity" title={canModerate ? "Remove this criterion from this project" : "Request removal (needs supervisor approval)"} onClick={(e) => { e.stopPropagation(); onRequestRemove(s.key); }} data-testid={`audit-remove-${s.key}`}><X className="h-3.5 w-3.5 text-red-400" /></button>
          )}
          <span className="text-[10px] font-semibold px-2 py-0.5 rounded shrink-0 uppercase tracking-wide" style={s.auto ? { color: "#0044FF", background: "#0044FF14" } : { color: "#7C3AED", background: "#7C3AED14" }}>
            {s.auto ? "Auto" : "Manual"}
          </span>
          {(s.weight || 1) > 1 && (
            <span className="text-[10px] font-bold px-1.5 py-0.5 rounded shrink-0 bg-secondary" title={`Weighted ${s.weight}× in the score`} data-testid={`audit-weight-${s.key}`}>{s.weight}×</span>
          )}
          <ChevronDown className={`h-4 w-4 shrink-0 text-muted-foreground transition-transform ${open ? "rotate-180" : ""}`} />
        </div>
        {open && (
          <div className={`px-4 pb-3 pt-2 ml-7 mr-4 mb-2 rounded-lg border ${s.met ? "border-emerald-200 bg-emerald-50/50" : "border-red-200 bg-red-50/40"}`} data-testid={`audit-explain-${s.key}`}>
            <p className="text-[10px] font-bold uppercase tracking-wider mt-1" style={{ color: s.met ? "#059669" : "#DC2626" }}>
              {s.met ? "Why this is done" : "Why this is not done"}
            </p>
            <p className="text-sm mt-1">{s.why || s.detail}</p>
            {(s.evidence || []).length > 0 && (
              <ul className="mt-2 space-y-1" data-testid={`audit-evidence-${s.key}`}>
                {s.evidence.map((e, i) => (
                  <li key={i} className="text-xs text-muted-foreground flex items-start gap-1.5">
                    <span className="h-1 w-1 rounded-full bg-current mt-1.5 shrink-0" />{e}
                  </li>
                ))}
              </ul>
            )}
            {!s.met && COACHING_TIPS[s.key] && (
              <p className="text-xs mt-2 pt-2 border-t border-red-200/60"><span className="font-bold">How to fix: </span>{COACHING_TIPS[s.key]}</p>
            )}
          </div>
        )}
      </div>
    );
  };
  return (
    <div className="border border-border rounded-lg overflow-hidden" data-testid="audit-checklist">
      <div className="px-4 py-2.5 bg-secondary/40 flex items-center justify-between">
        <p className="text-xs font-bold uppercase tracking-wide text-muted-foreground">Checked automatically</p>
        <p className="text-[10px] text-muted-foreground">Click any item to see why it's done or not</p>
      </div>
      {auto.map((s) => <Row key={s.key} s={s} />)}
      <div className="px-4 py-2.5 bg-secondary/40 border-t border-border"><p className="text-xs font-bold uppercase tracking-wide text-muted-foreground">PM confirmation</p></div>
      {manual.map((s) => <Row key={s.key} s={s} />)}
    </div>
  );
}

export function AuditScore({ audit }) {
  const color = scoreColor(audit.score);
  return (
    <div className="border border-border rounded-lg p-5 flex items-center gap-5" data-testid="audit-score">
      <div className="h-16 w-16 rounded-full flex items-center justify-center shrink-0" style={{ background: `${color}1a`, border: `3px solid ${color}` }}>
        <span className="font-display text-lg font-extrabold" style={{ color }}>{audit.score}%</span>
      </div>
      <div className="min-w-0 flex-1">
        <p className="font-display font-bold">PM score</p>
        <p className="text-xs text-muted-foreground">{audit.met} of {audit.total} project standards met</p>
        <div className="h-2 rounded-full bg-secondary overflow-hidden mt-2"><div className="h-full rounded-full transition-all" style={{ width: `${audit.score}%`, background: color }} /></div>
      </div>
    </div>
  );
}

export default function AuditView({ projectId }) {
  const { user } = useAuth();
  const isPM = !["supervisor", "admin"].includes(user?.role);
  const [audit, setAudit] = useState(null);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({ escalation_contact: "", audit_contact: "", audit_weekly_enabled: false });

  const load = async () => {
    try {
      const { data } = await api.get(`/projects/${projectId}/audit`);
      setAudit(data);
      setForm({ escalation_contact: data.escalation_contact, audit_contact: data.audit_contact, audit_weekly_enabled: data.audit_weekly_enabled });
      if (isPM) {
        const { idx, level } = levelFor(data);
        const key = `levelup-${projectId}`;
        const stored = localStorage.getItem(key);
        if (stored !== null && idx < Number(stored)) {
          fireConfetti();
          toast.success(`Level up! You've reached ${level.name}`, { description: "Your delivery practices moved up a level since last time." });
        }
        localStorage.setItem(key, String(idx));
      }
    } catch (e) { toast.error("Could not load audit"); }
  };
  useEffect(() => { load(); }, [projectId]); // eslint-disable-line react-hooks/exhaustive-deps

  const toggleManual = async (key, done) => {
    try {
      const prev = audit;
      const { data } = await api.post(`/projects/${projectId}/audit/manual`, { key, done });
      setAudit(data);
      if (isPM && prev && data.met > prev.met) {
        const before = levelFor(prev);
        const after = levelFor(data);
        localStorage.setItem(`levelup-${projectId}`, String(after.idx));
        if (after.idx < before.idx) {
          fireConfetti();
          toast.success(`Level up! You've reached ${after.level.name}`, { description: `${data.met} of ${data.total} practices in place — keep going!` });
        } else {
          const left = data.total - data.met;
          toast.success(left === 0 ? "All practices in place — Maestro Level!" : `Practice complete — ${left} to go`);
          if (left === 0) fireConfetti();
        }
      }
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Update failed"); }
  };

  const requestRemove = async (key) => {
    const reason = window.prompt("Why should this criterion be removed from this project's checklist?", "");
    if (reason === null) return;
    try {
      const { data } = await api.post(`/projects/${projectId}/audit/exclusions`, { key, reason });
      setAudit(data);
      toast.success(isPM ? "Removal requested — a supervisor needs to approve it" : "Criterion removed from this project's checklist");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not request removal"); }
  };

  const decideExclusion = async (key, approve) => {
    try {
      const { data } = await api.post(`/projects/${projectId}/audit/exclusions/${key}/decide`, { approve });
      setAudit(data);
      toast.success(approve ? "Removal approved — criterion no longer counts for this project" : "Removal rejected — criterion stays on the checklist");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not update"); }
  };

  const restoreCriterion = async (key) => {
    try {
      const { data } = await api.delete(`/projects/${projectId}/audit/exclusions/${key}`);
      setAudit(data);
      toast.success("Criterion restored to the checklist");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not restore"); }
  };

  const saveSettings = async () => {
    setSaving(true);
    try {
      const { data } = await api.put(`/projects/${projectId}/audit/settings`, form);
      setAudit(data);
      toast.success("Audit settings saved");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Save failed");
    } finally { setSaving(false); }
  };

  if (!audit) return <div className="flex justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>;

  return (
    <div className="space-y-6" data-testid="audit-view">
      <div className="flex justify-end -mb-2"><SeeItInAction chapter={17} /></div>
      <div className="grid lg:grid-cols-3 gap-4">
        <div className="lg:col-span-2 space-y-4">
          {isPM
            ? <LevelUpPanel audit={audit} projectId={projectId} onCoached={(c) => setAudit((a) => ({ ...a, coaching: c.coaching, coaching_at: c.coaching_at }))} />
            : <AuditScore audit={audit} />}
          <AuditChecklist audit={audit} onToggleManual={toggleManual} onRequestRemove={requestRemove} onDecideExclusion={decideExclusion} onRestore={restoreCriterion} canModerate={!isPM} />
        </div>
        <div className="space-y-4">
          <div className="border border-border rounded-lg p-5 space-y-4" data-testid="audit-settings">
            <h3 className="font-display font-bold text-sm flex items-center gap-2"><ClipboardCheck className="h-4 w-4 text-primary" /> Audit settings</h3>
            <div>
              <Label htmlFor="esc-contact" className="flex items-center gap-1.5 text-xs"><Siren className="h-3.5 w-3.5 text-red-500" /> Escalation contact</Label>
              <Input id="esc-contact" type="email" placeholder="escalations@company.com" value={form.escalation_contact} onChange={(e) => setForm((f) => ({ ...f, escalation_contact: e.target.value }))} data-testid="audit-escalation-input" className="mt-1.5" />
              <p className="text-[11px] text-muted-foreground mt-1">Emailed automatically (once per task) when a Jeopardy task slips past its end date — includes the task + linked risk summary.</p>
            </div>
            <div>
              <Label htmlFor="audit-contact" className="flex items-center gap-1.5 text-xs"><Mail className="h-3.5 w-3.5 text-primary" /> Audit point of contact</Label>
              <Input id="audit-contact" type="email" placeholder="manager@company.com" value={form.audit_contact} onChange={(e) => setForm((f) => ({ ...f, audit_contact: e.target.value }))} data-testid="audit-contact-input" className="mt-1.5" />
            </div>
            <label className="flex items-center justify-between gap-3 cursor-pointer">
              <div>
                <p className="text-sm font-semibold">Weekly audit email</p>
                <p className="text-[11px] text-muted-foreground">Sends this checklist + PM score every Monday.</p>
              </div>
              <Switch checked={form.audit_weekly_enabled} onCheckedChange={(v) => setForm((f) => ({ ...f, audit_weekly_enabled: !!v }))} data-testid="audit-weekly-switch" />
            </label>
            <Button className="w-full" onClick={saveSettings} disabled={saving} data-testid="audit-save-settings">
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save settings"}
            </Button>
          </div>
          <CustomCriteria onChanged={load} />
        </div>
      </div>
    </div>
  );
}

export function CustomCriteria({ onChanged }) {
  const { user } = useAuth();
  const canEdit = ["supervisor", "admin"].includes(user?.role);
  const [items, setItems] = useState([]);
  const [label, setLabel] = useState("");
  const [desc, setDesc] = useState("");
  const [weight, setWeight] = useState(1);
  const [adding, setAdding] = useState(false);

  const load = () => api.get("/audit/criteria").then(({ data }) => setItems(data)).catch(() => {});
  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const add = async () => {
    if (!label.trim()) { toast.error("Describe the criterion first"); return; }
    setAdding(true);
    try {
      const { data } = await api.post("/audit/criteria", { label, description: desc, weight });
      toast.success(data.mode === "auto"
        ? `Added as an AUTO check — the tool can track this (${data.rationale || "rule matched"})`
        : "Added as a MANUAL check — needs human confirmation per project");
      setLabel(""); setDesc(""); setWeight(1); load(); onChanged?.();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not add criterion");
    } finally { setAdding(false); }
  };

  const changeWeight = async (id, w) => {
    try { await api.patch(`/audit/criteria/${id}`, { weight: Number(w) }); load(); onChanged?.(); toast.success(`Weight set to ${w}×`); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Weight update failed"); }
  };

  const remove = async (id) => {
    try { await api.delete(`/audit/criteria/${id}`); load(); onChanged?.(); toast.success("Criterion removed"); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Delete failed"); }
  };

  return (
    <div className="border border-border rounded-lg p-5 space-y-3" data-testid="audit-custom-criteria">
      <h3 className="font-display font-bold text-sm flex items-center gap-2"><Wand2 className="h-4 w-4 text-primary" /> Custom audit criteria</h3>
      <p className="text-[11px] text-muted-foreground">The tool assesses each new criterion — if it can track it, it becomes an automatic check; otherwise it's a manual checkbox on every project's audit. Weight (1–3×) controls how much a criterion counts toward the score.</p>
      {items.length > 0 && (
        <div className="space-y-1.5">
          {items.map((c) => (
            <div key={c.id} className="flex items-center gap-2 text-sm border border-border rounded-lg px-2.5 py-1.5" data-testid={`custom-criterion-${c.id}`}>
              <span className="truncate flex-1 min-w-0">{c.label}</span>
              <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded uppercase shrink-0" style={c.mode === "auto" ? { color: "#0044FF", background: "#0044FF14" } : { color: "#7C3AED", background: "#7C3AED14" }}>{c.mode}</span>
              {canEdit ? (
                <select value={c.weight || 1} onChange={(e) => changeWeight(c.id, e.target.value)} className="text-[11px] font-bold border border-border rounded px-1 py-0.5 bg-background shrink-0 cursor-pointer" title="Score weight" data-testid={`custom-criterion-weight-${c.id}`}>
                  {[1, 2, 3].map((n) => <option key={n} value={n}>{n}×</option>)}
                </select>
              ) : (
                <span className="text-[10px] font-bold shrink-0">{c.weight || 1}×</span>
              )}
              {canEdit && <button onClick={() => remove(c.id)} className="text-red-500 shrink-0" data-testid={`custom-criterion-delete-${c.id}`}><Trash2 className="h-3.5 w-3.5" /></button>}
            </div>
          ))}
        </div>
      )}
      {canEdit ? (
        <div className="space-y-2">
          <Input placeholder='e.g. "At least 90% of tasks have dates"' value={label} onChange={(e) => setLabel(e.target.value)} data-testid="custom-criterion-label" />
          <Input placeholder="Optional detail" value={desc} onChange={(e) => setDesc(e.target.value)} data-testid="custom-criterion-desc" />
          <div className="flex items-center gap-1.5">
            <span className="text-[11px] text-muted-foreground mr-1">Weight</span>
            {[1, 2, 3].map((n) => (
              <button key={n} type="button" onClick={() => setWeight(n)}
                      className={`text-xs font-bold px-2.5 py-1 rounded-full border transition-colors ${weight === n ? "bg-primary text-primary-foreground border-primary" : "border-border hover:bg-secondary"}`}
                      data-testid={`custom-criterion-weight-new-${n}`}>{n}×</button>
            ))}
            <span className="text-[10px] text-muted-foreground ml-1">higher = bigger impact on scores</span>
          </div>
          <Button size="sm" className="w-full gap-1.5" onClick={add} disabled={adding} data-testid="custom-criterion-add">
            {adding ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />} {adding ? "Assessing…" : "Add criterion"}
          </Button>
        </div>
      ) : (
        <p className="text-[11px] text-muted-foreground italic">Supervisors and admins can add new criteria.</p>
      )}
    </div>
  );
}
